import { useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

function TeacherRegistration() {
  const { t } = useTranslation('teacherregistration');
  const { api, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [generalError, setGeneralError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    gender: 'MALE',
    email: '',
    phone: '',
    date_of_joining: '',
    id_number: '',
    date_of_birth: '',
    password: '',
    confirm_password: '',
    tsc_number: '',
    address: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear specific field error when user starts typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) errors.name = t('errors.name_required');
    if (!formData.email.trim()) errors.email = t('errors.email_required');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = t('errors.email_invalid');
    
    if (!formData.phone.trim()) errors.phone = t('errors.phone_required');
    if (!formData.id_number) errors.id_number = t('errors.id_number_required');
    if (!formData.date_of_birth) errors.date_of_birth = t('errors.date_of_birth_required');
    if (!formData.date_of_joining) errors.date_of_joining = t('errors.date_of_joining_required');
    
    if (!formData.password) errors.password = t('errors.password_required');
    else if (formData.password.length < 8) errors.password = t('errors.password_length');
    
    if (formData.password !== formData.confirm_password) {
      errors.confirm_password = t('errors.passwords_not_match');
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError('');
    setFormErrors({});
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    // Prepare data for API submission (remove confirm_password)
    const submissionData = { ...formData };
    delete submissionData.confirm_password;
    
    // Convert id_number to integer
    submissionData.id_number = parseInt(submissionData.id_number, 10);
    
    setIsLoading(true);
    
    try {
      const response = await api.post(
        `/api/v1/teachers/schools/${user.school_reg}/teachers`, 
        submissionData
      );
      
      setSuccess(true);
      // Reset form after successful submission
      setFormData({
        name: '',
        gender: 'MALE',
        email: '',
        phone: '',
        date_of_joining: '',
        id_number: '',
        date_of_birth: '',
        password: '',
        confirm_password: '',
        tsc_number: '',
        address: ''
      });
      
      // Show success message for 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error("Registration error:", error);
      
      // Handle different types of API errors
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const { data } = error.response;
        
        if (data.detail) {
          if (typeof data.detail === 'string') {
            setGeneralError(data.detail);
          } else if (Array.isArray(data.detail)) {
            // Handle FastAPI validation errors
            const fieldErrors = {};
            data.detail.forEach(err => {
              const field = err.loc[err.loc.length - 1];
              fieldErrors[field] = err.msg;
            });
            setFormErrors(fieldErrors);
          }
        } else if (data.errors) {
          setFormErrors(data.errors);
        } else {
          setGeneralError(t('errors.registration_error'));
        }
      } else if (error.request) {
        // The request was made but no response was received
        setGeneralError(t('errors.no_response'));
      } else {
        // Something happened in setting up the request
        setGeneralError(t('errors.process_request'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md flex items-center text-green-700">
          <CheckCircle className="mr-2 h-5 w-5" />
          <span>{t('success.registration')}</span>
        </div>
      )}
      
      {generalError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
          <AlertCircle className="mr-2 h-5 w-5" />
          <span>{generalError}</span>
        </div>
      )}

      <h2 className="text-2xl font-semibold text-gray-800 mb-6">{t('title')}</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Full Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              {t('fields.name')} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                formErrors.name 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                  : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
              }`}
              placeholder={t('placeholders.name')}
            />
            {formErrors.name && (
              <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
            )}
          </div>

          {/* Gender */}
          <div>
            <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">
              {t('fields.gender')} *
            </label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-500"
            >
              <option value="MALE">{t('gender_options.male')}</option>
              <option value="FEMALE">{t('gender_options.female')}</option>
            </select>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              {t('fields.email')} *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                formErrors.email 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                  : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
              }`}
              placeholder={t('placeholders.email')}
            />
            {formErrors.email && (
              <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              {t('fields.phone')} *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                formErrors.phone 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                  : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
              }`}
              placeholder={t('placeholders.phone')}
            />
            {formErrors.phone && (
              <p className="mt-1 text-sm text-red-600">{formErrors.phone}</p>
            )}
          </div>

          {/* ID Number */}
          <div>
            <label htmlFor="id_number" className="block text-sm font-medium text-gray-700 mb-1">
              {t('fields.id_number')} *
            </label>
            <input
              type="number"
              id="id_number"
              name="id_number"
              value={formData.id_number}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                formErrors.id_number 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                  : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
              }`}
              placeholder={t('placeholders.id_number')}
            />
            {formErrors.id_number && (
              <p className="mt-1 text-sm text-red-600">{formErrors.id_number}</p>
            )}
          </div>

          {/* TSC Number (Optional) */}
          <div>
            <label htmlFor="tsc_number" className="block text-sm font-medium text-gray-700 mb-1">
              {t('fields.tsc_number')} ({t('common.optional')})
            </label>
            <input
              type="text"
              id="tsc_number"
              name="tsc_number"
              value={formData.tsc_number}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-500"
              placeholder={t('placeholders.tsc_number')}
            />
          </div>

          {/* Date of Birth */}
          <div>
            <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700 mb-1">
              {t('fields.date_of_birth')} *
            </label>
            <div className="relative">
              <input
                type="date"
                id="date_of_birth"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                  formErrors.date_of_birth 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                    : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
                }`}
              />
            </div>
            {formErrors.date_of_birth && (
              <p className="mt-1 text-sm text-red-600">{formErrors.date_of_birth}</p>
            )}
          </div>

          {/* Date of Joining */}
          <div>
            <label htmlFor="date_of_joining" className="block text-sm font-medium text-gray-700 mb-1">
              {t('fields.date_of_joining')} *
            </label>
            <div className="relative">
              <input
                type="date"
                id="date_of_joining"
                name="date_of_joining"
                value={formData.date_of_joining}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                  formErrors.date_of_joining 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                    : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
                }`}
              />
            </div>
            {formErrors.date_of_joining && (
              <p className="mt-1 text-sm text-red-600">{formErrors.date_of_joining}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              {t('fields.password')} *
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                formErrors.password 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                  : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
              }`}
              placeholder={t('placeholders.password')}
            />
            {formErrors.password && (
              <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
              {t('fields.confirm_password')} *
            </label>
            <input
              type="password"
              id="confirm_password"
              name="confirm_password"
              value={formData.confirm_password}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                formErrors.confirm_password 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                  : 'border-gray-300 focus:border-teal-500 focus:ring-teal-200'
              }`}
              placeholder={t('placeholders.confirm_password')}
            />
            {formErrors.confirm_password && (
              <p className="mt-1 text-sm text-red-600">{formErrors.confirm_password}</p>
            )}
          </div>
        </div>

        {/* Address (Optional) - Full width */}
        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
            {t('fields.address')} ({t('common.optional')})
          </label>
          <textarea
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            rows="3"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-500"
            placeholder={t('placeholders.address')}
          ></textarea>
        </div>

        {/* Submit Button */}
        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-teal-700 hover:bg-teal-800 text-white font-medium py-2.5 px-4 rounded-md transition duration-300 flex items-center justify-center"
            style={{ backgroundColor: '#0E5252' }}
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin mr-2 h-5 w-5" />
                {t('buttons.registering')}
              </>
            ) : (
              t('buttons.register')
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default TeacherRegistration;